import CityController from "../../../controller/city.controller";
import { GetServerSideProps, NextPage } from "next";
import useQuery from "../../../hooks/useQuery";
import React, { Suspense, useState, useEffect } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import { ListingPageProps } from "../../../types/ListingPage.interface";
import VerifyModal from "../../../components/VerifyModal";
import { Property } from "../../../types/Property.interface";
import useStore from "../../../context/useStore";

const DesktopView = dynamic(
  () => import("../../../Layout/CityPage/DesktopCityPage"),
  {
    suspense: true,
  }
);

const MobileView = dynamic(
  () => import("../../../Layout/CityPage/MobileCityPage"),
  {
    suspense: true,
  }
);
const Filters = dynamic(() => import("../../../components/Listing/Filters"), {
  suspense: true,
});
const City: NextPage<ListingPageProps> = ({
  query,
  city,
  properties,
  isMobile,
  neighbourhoods,
}) => {
  const queries = useQuery(query);
  const [visible, setVisible] = useState(false);

  const { verify, setVerify } = useStore();
  const onClose = () => {
    setVerify({
      open: false,
      closable: true,
    });
  };
  useEffect(() => {
    setVisible(verify.open);
  }, [verify.open]);

  return (
    <>
      <Head>
        <title>{city.title}</title>
        <meta
          name="title"
          property="og:title"
          content={(city.title && city.title) || ""}
        />

        <meta
          name="description"
          property="og:description"
          content={(city.desc && city.desc) || ""}
        />
        <meta
          name="keywords"
          content={city.keywords ? `${city.keywords}` : ``}
        />

        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://bungalowfinder.ca/city/${city.slug}`}
        />
        <link
          rel="canonical"
          href={`https://bungalowfinder.ca/city/${city.slug.toUpperCase()}`}
        />

        <meta
          property="og:image"
          itemProp="image"
          content={`https://api.bungalowfinder.ca` + city.image?.images.og.url}
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content={(city.title && city.title) || ""}
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={(city.title && city.title) || ""} />
        <meta
          name="twitter:description"
          content={(city.desc && city.desc) || ""}
        />
        <meta
          name="twitter:image"
          content={`https://api.bungalowfinder.ca` + city.image?.images.og.url}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `${(city.jsonLD && city.jsonLD) || ""}`,
          }}
        ></script>
        {properties.property.map((item: Property) => (
          <script
            key={item.ListingKey}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `[{
  "@context": "http://schema.org",
  "@type": "SingleFamilyResidence",
  "name": "${item.UnparsedAddress ? item.UnparsedAddress : item.StreetName + " " + item.StreetNumber + " " + item.StreetSuffix + " " + item.City + " " + item.StateOrProvince}",
  "numberOfBathroomsTotal": "${item.BathroomsTotalInteger}",
  "numberOfBedrooms": "${item.BedroomsTotal}",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "${item.UnparsedAddress ? item.UnparsedAddress : item.StreetName + " " + item.StreetNumber + " " + item.StreetSuffix + " " + item.City + " " + item.StateOrProvince}",
    "addressLocality": "${item.City}",
    "addressRegion": "ON",
    "addressCountry": "CA"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "${item.Lat}",
    "longitude": "${item.Lng}"
  },
  "image":"${item.Image}",
  "offers": {
    "@type": "Offer",
    "price": "${item.ListPrice}",
    "priceCurrency": "CAD",
    "availability": "https://schema.org/InStock"
  },
  "url": "https://bungalowfinder.ca/propertydetails/${item.Slug}"
},
              {
        "@context": "http://schema.org",
        "@type": "Product",
         "description": "This ${item.BedroomsTotal}-bed, 2-${item.BathroomsTotalInteger} property in ${
                item.UnparsedAddress ? item.UnparsedAddress : item.StreetName + " " + item.StreetNumber + " " + item.StreetSuffix + " " + item.City + " " + item.StateOrProvince
              } with ${item.ParkingTotal} parkings is now on sale for ${
                item.ListPrice
              }.",
        "name": "${item.UnparsedAddress ? item.UnparsedAddress : item.StreetName + " " + item.StreetNumber + " " + item.StreetSuffix + " " + item.City + " " + item.StateOrProvince}",
        "offers": {
            "@type": "Offer",
            "price": "${item.ListPrice}",
            "url":"https://bungalowfinder.ca/propertydetails/${item.Slug}",
            "priceCurrency": "CAD"
        },
        "image": "${item.Image}"
            }]
`,
            }}
          />
        ))}
      </Head>
      <div className="flex-1 flex flex-col">
        <div className="bg-primary z-10">
          <div className="bg-primary mix-blend-screen text-white py-3">
            <div className="container mx-auto flex justify-end">
              <Suspense
                fallback={
                  <>
                    <p>Loading...</p>
                  </>
                }
              >
                <Filters query={query} queries={queries} isMobile={isMobile} />
              </Suspense>
            </div>
          </div>
        </div>
        {isMobile ? (
          <Suspense>
            <MobileView
              query={query}
              city={city}
              properties={properties}
              neighbourhoods={neighbourhoods}
            />
          </Suspense>
        ) : (
          <Suspense>
            <DesktopView
              query={query}
              city={city}
              properties={properties}
              neighbourhoods={neighbourhoods}
            />
          </Suspense>
        )}
      </div>
      <VerifyModal open={visible} onClose={onClose} />
    </>
  );
};

export default City;

export const getServerSideProps: GetServerSideProps = async (context) => {
  return await CityController(context);
};
